import { useAppSelector } from '@/store';

import type { SessionViewer } from '../components/EngagementHub/roles';
import type { PartyIdentityView } from './parties';

/**
 * Real session identity for `roles.ts`, replacing the prototype's
 * `FULFILLMENT_USER_IDS` allowlist and the Hub's "Viewing as" chips.
 *
 * `isOfStaff` reads `viewer.admin` — the only OnFrontiers-side signal the
 * session carries today. It is deliberately narrow: an OF user who is not an
 * admin resolves to their project-derived role instead of `of_fulfillment`,
 * which costs them the passive-observer affordances but never grants an
 * affordance to someone who shouldn't have it. The dispute-resolution surface
 * those affordances gate is FE3, so nothing in FE2a depends on widening it; a
 * dedicated fulfillment capability is the eventual home.
 */
export function useSessionViewer(): SessionViewer {
  const viewer = useAppSelector((state) => state.viewer);
  return { userId: viewer.id ?? '', isOfStaff: viewer.admin === true };
}

/** The viewer's own expert profile id, when they have one. */
export function useViewerProfileId(): string | null {
  return useAppSelector((state) => state.viewer.profile?.id ?? null);
}

/**
 * Whether the viewer is OnFrontiers internal staff — ENG-5684's gate for the
 * staff-wide engagements tab.
 *
 * A DISPLAY gate, not an authorization one: `ListStaffEngagements` denies a
 * non-staff caller outright. This only decides which entry point renders.
 *
 * **On today's code this returns the same answer as `viewer.admin` for every
 * viewer who can reach this surface**, so do not read the choice below as
 * fixing a live bug. `gate.ts`'s `canViewEngagementSurfaces` short-circuits on
 * `viewer.admin`, so every staffer who gets here already has it true; and
 * `GET_VIEWER` selects `admin` and `groups` in the SAME document with Apollo's
 * default errorPolicy, so if `admin` errored the whole query would reject and
 * `fetchViewer` would log the user out before this hook ran at all.
 *
 * It is still the right predicate, for two reasons that are about correctness
 * rather than behavior:
 *
 * 1. It names the actual question. `User.admin` resolves through
 *    `permission.CheckOld(ctx, "-", "-", "-")` — a permission probe that means
 *    "is staff" only by convention.
 * 2. It does not inherit ENG-5695. `admin`'s path runs the global "*:*" staff
 *    policy, whose `staff_role` operand returns `FindStaffRole`'s error
 *    verbatim, and that function ERRORS (rather than returning "no role") on
 *    any team role outside member|admin|owner — `viewer` is assignable on an
 *    enterprise team from the ordinary team-admin screen — and on membership in
 *    two internal teams. When ENG-5695 is fixed so those users can log in
 *    instead of being locked out, this hook is already correct for them and
 *    `admin` would still be false.
 *
 * The predicate itself: `User.groups` searches with MemberOnly — a
 * `team_members` row joined on `state = 'active'` — and passes no internal
 * filter, so an internal team the viewer actively belongs to is already in the
 * list. That is the same condition as the api's own `isInternalStaff`, so the
 * two cannot disagree about who is staff.
 *
 * `useSessionViewer().isOfStaff` is left alone on purpose: it feeds `roles.ts`
 * → `deriveViewerRole` → `of_fulfillment` and the Hub's passive-observer
 * affordances. Widening it is a Hub behavior change, tracked separately.
 */
export function useIsInternalStaff(): boolean {
  const groups = useAppSelector((state) => state.viewer.groups);
  return (groups ?? []).some((group) => group?.internal === true);
}

/**
 * The viewer's own display identity, as the last-resort name source for
 * `partyNamer` (ENG-5733).
 *
 * Needed only because dropping the "You" short-circuit exposed a gap it was
 * hiding: an account-level governance approver is on no project and is not the
 * expert, so nothing in the engagement read model names them and their own rows
 * read "A participant". Null when the session carries no profile, which leaves
 * that fallback in place rather than inventing a name.
 */
export function useSelfIdentity(): PartyIdentityView | null {
  const profile = useAppSelector((state) => state.viewer.profile);
  // Truthy, not `??`: `Profile.name` is composed server-side from first/last and
  // arrives as '' for a profile with none, which `??` would take as
  // authoritative and skip the compose below — leaving the viewer reading their
  // own rows as "A participant" with a usable name sitting beside it.
  const name =
    profile?.name?.trim() ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim();
  if (!name) return null;
  return {
    name,
    pictureUrl: profile?.picture_url ?? null,
    isOnFrontiers: false,
    nameIsBrand: false,
  };
}
